"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import TimestampInput from "@/components/ui/timestamp-input";
import { client } from "@/lib/hono";
import toast from "@/lib/toast";
import { cn } from "@/lib/utils";
import { VideoChapter } from "@yedalabs/schema";
import { useQueryClient } from "@tanstack/react-query";
import { LoaderCircle, Plus, Trash } from "lucide-react";
import { useEffect, useState } from "react";
import VideoWorkflowStep from "../videoWorkflow/videoWorkflowStep";
import { useVideoWorkflow } from "../videoWorkflow/videoWorkflowProvider";
import { t } from "@/lib/localization";

export default function ChaptersTab({
    videoId,
    initialChapters,
    lengthInSeconds,
    setReloadVideoKey,
}: {
    videoId: string;
    initialChapters: VideoChapter[];
    lengthInSeconds: number;
    setReloadVideoKey: (key: number) => void;
}) {
    const [chapters, setChapters] = useState<VideoChapter[]>(initialChapters);
    const [changes, setChanges] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setChapters(initialChapters);
    }, [initialChapters]);

    const queryClient = useQueryClient();
    const { shouldShowWorkflowStepComponent } = useVideoWorkflow();

    const handleAddChapter = () => {
        setChanges(true);
        setChapters([...chapters, { title: "", start: 0, end: 0 }]);
    };

    const handleDeleteChapter = (index: number) => {
        setChanges(true);
        setChapters(chapters.filter((_, idx) => idx !== index));
    };

    const handleChange = (
        index: number,
        field: keyof VideoChapter,
        value: string | number
    ) => {
        setChanges(true);
        const updatedChapters = [...chapters];
        if (field === "start" || field === "end") {
            updatedChapters[index][field] = (value || 0) as number;
        } else {
            updatedChapters[index][field] = value as string;
        }
        setChapters(updatedChapters);
    };

    const handleSave = async () => {
        // Validation
        for (let i = 0; i < chapters.length; i++) {
            const chapter = chapters[i];

            // Check if start is before end
            if (chapter.start >= chapter.end) {
                toast.error(t("toast.error.start-before-end"), {
                    keyName: "toast.error.start-before-end",
                });
                return;
            }

            // Check if start and end are within the video length
            if (
                chapter.start > lengthInSeconds ||
                chapter.end > lengthInSeconds
            ) {
                toast.error(t("toast.error.exceeds-video-length"), {
                    keyName: "toast.error.exceeds-video-length",
                });
                return;
            }

            // Check if start of current chapter is after the end of the previous chapter
            if (i > 0 && chapter.start <= chapters[i - 1].end) {
                toast.error(t("toast.error.overlapping-chapter"), {
                    keyName: "toast.error.overlapping-chapter",
                });
                return;
            }

            // Check if end of current chapter is before the start of the next chapter
            if (
                i < chapters.length - 1 &&
                chapter.end >= chapters[i + 1].start
            ) {
                toast.error(t("toast.error.overlapping-next-chapter"), {
                    keyName: "toast.error.overlapping-next-chapter",
                });
                return;
            }
        }

        setLoading(true);

        const res = await client.api.storage.video[":videoId"].update.$post({
            param: { videoId },
            json: {
                chapters: JSON.stringify(chapters),
            },
        });

        const resJson = await res.json();
        console.log({ resJson });

        if (!res.ok) {
            toast.error(t("toast.error.update-video"), {
                keyName: "toast.error.update-video",
            });
            setLoading(false);
            return;
        }

        queryClient.invalidateQueries({
            queryKey: ["get-video", videoId],
        });

        setReloadVideoKey(Date.now());
        toast.success(t("toast.success.changes-saved"), {
            keyName: "toast.success.changes-saved",
        });
        setLoading(false);
        setChanges(false);
    };

    const createChapters = async () => {
        const res = await client.api.ai["generate-video-chapters"].$post({
            json: {
                videoId,
            },
        });
        const data = await res.json();
        console.log({ data });
        if (!res.ok) {
            toast.error(t("toast.error.default"), {
                keyName: "toast.error.default",
            });
        } else {
            toast.success(t("toast.success.chapters-generated"), {
                keyName: "toast.success.chapters-generated",
            });
            queryClient.invalidateQueries({
                queryKey: ["get-video", videoId],
            });
            setReloadVideoKey(Date.now());
        }
    };

    return (
        <div className="mt-4">
            <div className="flex flex-row gap-2 justify-between items-center">
                <div>
                    <Label className="">
                        {t(
                            "video-sheet.chapters-tab.title",
                            undefined,
                            undefined
                        )}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                        {t(
                            "video-sheet.chapters-tab.description",
                            undefined,
                            undefined
                        )}
                    </p>
                </div>
                <Button
                    size="sm"
                    variant="outline"
                    className="flex flex-row items-center gap-1 w-fit"
                    onClick={handleAddChapter}
                >
                    <Plus className="size-4" />
                    {t("video-sheet.chapters-tab.add-btn")}
                </Button>
            </div>

            <div className="flex flex-col gap-6">
                <div
                    className={cn(
                        "flex w-full flex-col gap-2",
                        chapters.length > 0 && "mt-6"
                    )}
                >
                    {chapters.map((chapter, idx) => (
                        <div
                            key={idx}
                            className="flex flex-row items-center gap-2 text-sm"
                        >
                            <div className="flex flex-row gap-2 items-center">
                                <span className="text-muted-foreground w-2">
                                    {idx + 1}
                                </span>

                                <TimestampInput
                                    valueInSeconds={chapter.start}
                                    onValueChange={(value) => {
                                        handleChange(idx, "start", value);
                                    }}
                                    triggerClassName="w-[85px]"
                                />

                                <span className="text-muted-foreground">
                                    {t(
                                        "video-sheet.chapters-tab.chapter.to",
                                        undefined,
                                        undefined
                                    )}
                                </span>

                                <TimestampInput
                                    valueInSeconds={chapter.end}
                                    onValueChange={(value) => {
                                        handleChange(idx, "end", value);
                                    }}
                                    triggerClassName="w-[85px]"
                                />
                            </div>

                            <div className="flex flex-row w-full gap-2 items-center">
                                <Input
                                    value={chapter.title}
                                    onChange={(e) =>
                                        handleChange(
                                            idx,
                                            "title",
                                            e.target.value
                                        )
                                    }
                                    placeholder={t(
                                        "video-sheet.chapters-tab.chapter.placeholder",
                                        undefined,
                                        undefined
                                    )}
                                />
                                <button
                                    onClick={() => handleDeleteChapter(idx)}
                                    className="text-muted-foreground hover:text-black p-1"
                                >
                                    <Trash className="size-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                {/* <Button onClick={createChapters}>Create chapters</Button> */}

                {changes && (
                    <Button
                        disabled={loading || !changes}
                        onClick={handleSave}
                        className="w-fit"
                    >
                        {loading ? (
                            <LoaderCircle className="size-4 animate-spin" />
                        ) : (
                            t(
                                "video-sheet.chapters-tab.save-btn",
                                undefined,
                                undefined
                            )
                        )}
                    </Button>
                )}

                {shouldShowWorkflowStepComponent("chapters") && (
                    <VideoWorkflowStep
                        stepName="chapters"
                        texts={{
                            title: {
                                keyName:
                                    "video-sheet.create-chapters-with-ai.title",
                                defaultValue: "יצירת פרקים עם AI",
                            },
                            description: {
                                keyName:
                                    "video-sheet.create-chapters-with-ai.description",
                                defaultValue:
                                    "לחצו כאן ובתוך כמה שניות יהיו מוכנים פרקים מדוייקים לסרטון",
                            },
                            generateBtn: {
                                keyName:
                                    "video-sheet.generate-chapters-with-ai-btn",
                                defaultValue: "יצירה",
                            },
                        }}
                    />
                )}
            </div>
        </div>
    );
}
